import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { KeyRound, LogOut } from 'lucide-react';

export const ForcePasswordChange = () => {
  const { user, setUser, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (pw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pw !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: pw });
      toast.success('Password updated — welcome aboard!');
      setUser({ ...user, must_change_password: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-border/60">
        <CardHeader>
          <div className="flex items-center gap-2 text-amber-500 mb-1">
            <KeyRound size={18}/>
            <span className="text-xs font-semibold uppercase tracking-wide">First login — action required</span>
          </div>
          <CardTitle className="text-xl">Set your own password</CardTitle>
          <p className="text-sm text-muted-foreground">
            Hi {user?.name?.split(' ')[0] || 'there'} — for security, replace the temporary password
            you received before continuing.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="fpc-current">Temporary (current) password</Label>
              <Input id="fpc-current" type="password" value={current} onChange={e => setCurrent(e.target.value)} required data-testid="fpc-current-password"/>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fpc-new">New password (min 8 characters)</Label>
              <Input id="fpc-new" type="password" value={pw} onChange={e => setPw(e.target.value)} required data-testid="fpc-new-password"/>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fpc-confirm">Confirm new password</Label>
              <Input id="fpc-confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="fpc-confirm-password"/>
            </div>
            <Button type="submit" className="w-full font-semibold" disabled={loading} data-testid="fpc-submit">
              {loading ? 'Updating…' : 'Set password & continue'}
            </Button>
            <button type="button" onClick={logout} className="w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 pt-1" data-testid="fpc-logout">
              <LogOut size={12}/> Sign out instead
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
